import React, { useMemo } from "react";

// Sunday-first, matching the dashboard-wide convention (DateRangePicker,
// Website Analytics, Google Ads Day of Week).
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// Data uses ISO dow (1=Mon … 7=Sun); % 7 maps Sun→0, Mon→1 … Sat→6.
const isoToSundayFirst = (dow) => dow % 7;

const DayOfWeekPanel = ({ headlinePct, matrix, loading }) => {
  const { grid, maxSessions } = useMemo(() => {
    if (!matrix?.length) return { grid: [], maxSessions: 0 };

    const g = Array.from({ length: 7 }, () => Array(7).fill(0));
    let max = 0;

    for (const row of matrix) {
      const si = isoToSundayFirst(row.search_dow);
      const ci = isoToSundayFirst(row.checkin_dow);
      if (si >= 0 && si < 7 && ci >= 0 && ci < 7) {
        g[si][ci] = row.sessions;
        if (row.sessions > max) max = row.sessions;
      }
    }

    return { grid: g, maxSessions: max };
  }, [matrix]);

  const getCellStyle = (sessions) => {
    if (!sessions || maxSessions === 0) return { backgroundColor: "rgba(229,231,235,0.3)" };
    const intensity = sessions / maxSessions;
    const alpha = 0.12 + intensity * 0.78;
    return { backgroundColor: `rgba(129, 140, 248, ${alpha})` };
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-1/2 mb-3" />
        <div className="grid grid-cols-8 gap-1">
          {Array.from({ length: 64 }).map((_, i) => (
            <div key={i} className="h-7 bg-gray-200 dark:bg-gray-800 rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!matrix?.length) {
    return (
      <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
        No search vs. stay data for this period.
      </div>
    );
  }

  return (
    <div>
      {/* KPI callout */}
      <div className="mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{headlinePct}%</div>
        <div className="text-sm text-gray-500 dark:text-gray-400">of weekend stays are searched Tue–Thu</div>
      </div>

      {/* Matrix */}
      <div>
        {/* Column header */}
        <div className="flex items-center mb-1">
          <div className="w-12 shrink-0" />
          <div className="flex-1 text-center text-sm font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            Check-in Day
          </div>
        </div>

        {/* Column day labels + Total header */}
        <div className="flex items-center gap-1 mb-1">
          <div className="w-12 shrink-0" />
          {DAY_LABELS.map((d) => (
            <div key={d} className="flex-1 text-center text-sm font-semibold text-gray-500 dark:text-gray-400">
              {d}
            </div>
          ))}
          <div className="w-12 shrink-0 text-center text-sm font-bold text-gray-400 dark:text-gray-500">Total</div>
        </div>

        {/* Rows with row totals */}
        {grid.map((row, si) => {
          const rowTotal = row.reduce((sum, v) => sum + v, 0);
          return (
          <div key={si} className="flex items-center gap-1 mb-1">
            <div className="w-12 shrink-0 text-right pr-2">
              <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">{DAY_LABELS[si]}</span>
            </div>
            {row.map((sessions, ci) => (
              <div
                key={ci}
                className="flex-1 h-7 rounded flex items-center justify-center transition-colors"
                style={getCellStyle(sessions)}
                title={`Searched: ${DAY_LABELS[si]}\nCheck-in: ${DAY_LABELS[ci]}\n${sessions} sessions`}
              >
                {sessions > 0 && (
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{sessions}</span>
                )}
              </div>
            ))}
            <div className="w-12 shrink-0 h-7 flex items-center justify-center">
              <span className="text-sm font-bold text-gray-500 dark:text-gray-400">{rowTotal}</span>
            </div>
          </div>
          );
        })}

        {/* Total row */}
        {grid.length > 0 && (
          <div className="flex items-center gap-1 mt-1 pt-1 border-t border-gray-200 dark:border-gray-700">
            <div className="w-12 shrink-0 text-right pr-2">
              <span className="text-sm font-bold text-gray-400 dark:text-gray-500">Total</span>
            </div>
            {Array.from({ length: 7 }, (_, ci) => {
              const colTotal = grid.reduce((sum, row) => sum + row[ci], 0);
              return (
                <div key={ci} className="flex-1 h-7 flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-500 dark:text-gray-400">{colTotal}</span>
                </div>
              );
            })}
            <div className="w-12 shrink-0 h-7 flex items-center justify-center">
              <span className="text-sm font-bold text-gray-600 dark:text-gray-300">
                {grid.reduce((sum, row) => sum + row.reduce((s, v) => s + v, 0), 0)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
        <span className="text-sm text-gray-500 dark:text-gray-400 font-semibold uppercase whitespace-nowrap">Searched ↓</span>
        <span className="text-sm text-gray-500 dark:text-gray-400 font-semibold uppercase whitespace-nowrap">Check-in →</span>
        <div className="hidden min-[450px]:block flex-1" />
        <div className="flex items-center gap-3 max-[449px]:basis-full">
          <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">Less</span>
          <div className="flex gap-0.5 shrink-0">
            <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "rgba(129,140,248,0.12)" }} />
            <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "rgba(129,140,248,0.38)" }} />
            <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "rgba(129,140,248,0.65)" }} />
            <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: "rgba(129,140,248,0.90)" }} />
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">More</span>
        </div>
      </div>
    </div>
  );
};

export default DayOfWeekPanel;
